/**
 * NH 나무 PLUG API — /v1/nhplug/*
 * 잔고·판정·자동매매 설정·신호 기록
 */
import { apiClient } from "./client";

// ── 자동매매 설정 ──
export interface NhAutoConfig {
  mode: "off" | "dry_run" | "live";
  act_no: string;
  market: string;
  per_buy_amount: number;
  interval_minutes: number;
  sell_rsi_threshold: number;
  updated_at: string;
}

// ── 판정 현황 ──
export interface NhJudgmentCategory {
  category_key: string;
  label: string;
  us_ticker: string;
  current_pct: number;
  target_pct: number | null;
  dev_pct: number | null;
  dd_52w: number | null;
  rsi14: number | null;
  last_price: number | null;
  signal: "wait" | "buy" | "buy_x2" | "sell";
}

export interface NhJudgment {
  mode: string;
  band_pct: number;
  gate: { entry_dd: number; accel_dd: number; window_days: number };
  total_value_krw: number;
  fx_usd: number;
  categories: NhJudgmentCategory[];
}

// ── 신호 기록 ──
export interface NhSignal {
  id: number;
  signal_date: string;
  category_key: string;
  category_label: string | null;
  ticker: string;
  side: string;
  qty: number | null;
  amount_usd: number | null;
  mode: string;
  current_pct: number | null;
  target_pct: number | null;
  dev_pct: number | null;
  dd_52w: number | null;
  rsi14: number | null;
  price_at_signal: number | null;
  order_no: string | null;
  status: string;
  rsp_msg: string | null;
  created_at: string;
}

// ── 해외 잔고 ──
export interface NhOverseasPosition {
  iem_cd: string;
  iem_nm: string;
  cns_bse_bnc_qty: number;
  fc_avg_phs_pr: number;
  fc_sec_end_pr: number;
  fc_eal_amt: number;
  krw_eal_amt: number;
  eal_pft_rt: number;
}

// ── 해외 주문내역 ──
export interface NhOrderRow {
  orr_no: number;
  org_orr_no: number;
  oss_sby_dit_cd: string; // 1=매도 2=매수
  sby_dit_nm: string;
  iem_cd: string;
  tck_iem_cd: string;
  iem_nm: string;
  orr_qty: number;
  fc_orr_uit_pr: number;
  cns_qty: number;
  cns_pr: number;
  ny_cns_orr_qty: number;
  cor_can_dit_nm: string; // 정상 | 취소 | 정정
  oss_ato_orr_sts_cd: string;
  orr_sts_nm: string;
  ahi_nmn_pr_tp_cd: string;
  ahi_nmn_pr_tp_cd_nm: string;
  orr_dt: string;
  prev_close: number | null;
  pct_vs_prev: number | null;
  [key: string]: unknown;
}

export interface NhOrdersHistory {
  act_no: string;
  days: number;
  prev_close: Record<string, number>;
  summary: { pending_buy_usd: number; pending_sell_usd: number };
  orders: NhOrderRow[];
}

export interface NhQuote {
  iem_nm?: string;
  trdprc?: number;
  netchng?: number;
  pctchng?: number;
  [key: string]: unknown;
}

export interface NhOverseasBalance {
  act_no: string;
  rsp_cd: string;
  rsp_msg: string;
  summary: {
    fc_dca?: number;
    fc_eal_amt?: number;
    eal_amt_sum?: number;
    eal_pls_sum_amt?: number;
    krw_dca?: number;
    tot_aet_amt?: number;
    pft_rt?: number;
  } | null;
  positions: NhOverseasPosition[];
}

export const nhApi = {
  getAutoConfig: () => apiClient.get<NhAutoConfig>("/v1/nhplug/auto/config"),

  updateAutoConfig: (body: Partial<Pick<NhAutoConfig, "mode" | "per_buy_amount" | "interval_minutes" | "sell_rsi_threshold" | "act_no">>) =>
    apiClient.put<NhAutoConfig>("/v1/nhplug/auto/config", body),

  getJudgment: () => apiClient.get<NhJudgment>("/v1/nhplug/auto/judgment"),

  getSignals: (limit = 50) =>
    apiClient.get<{ signals: NhSignal[] }>("/v1/nhplug/auto/signals", { limit }),

  runOnce: () => apiClient.post<{ mode: string; signals: any[]; fills: any }>("/v1/nhplug/auto/run"),

  getOverseasBalance: (actNo?: string) =>
    apiClient.get<NhOverseasBalance>("/v1/nhplug/overseas/balance", actNo ? { act_no: actNo } : undefined),

  getDomesticBalance: (actNo?: string) =>
    apiClient.get<{
      act_no: string;
      rsp_msg: string;
      summary: { dca?: number; tot_eal_amt?: number; nas_amt?: number } | null;
      positions: any[];
    }>("/v1/nhplug/balance", actNo ? { act_no: actNo } : undefined),

  // ── 수동 주문 ──
  getOrdersHistory: (days = 10, execDit = "0", buySellDit = "0", actNo?: string) =>
    apiClient.get<NhOrdersHistory>("/v1/nhplug/overseas/orders/history", {
      days,
      exec_dit: execDit,
      buy_sell_dit: buySellDit,
      ...(actNo ? { act_no: actNo } : {}),
    }),

  getQuote: (ticker: string) =>
    apiClient.get<{ iem_cd: string; rsp_msg: string; quote: NhQuote | null }>(
      "/v1/nhplug/overseas/price",
      { iem_cd: ticker },
    ),

  placeOrder: (body: {
    side: "buy" | "sell";
    iem_cd: string;
    qty: number;
    price?: number | null;
    order_type: string;
    dry_run?: boolean;
    act_no?: string;
  }) => apiClient.post<{ rsp_msg?: string; order?: Record<string, unknown> } & Record<string, unknown>>("/v1/nhplug/overseas/order", body),

  cancelOrder: (body: { org_order_no: string; iem_cd: string; qty?: number; act_no?: string }) =>
    apiClient.post<{ rsp_msg?: string }>("/v1/nhplug/overseas/order/cancel", body),

  modifyOrder: (body: { org_order_no: string; iem_cd: string; price: number; act_no?: string }) =>
    apiClient.post<{ rsp_msg?: string; order?: Record<string, unknown> }>("/v1/nhplug/overseas/order/modify", body),

  getQuotes: (tickers: string[]) =>
    apiClient.get<{ quotes: Record<string, { trdprc: number; pctchng: number; prev_close: number | null }> }>(
      "/v1/nhplug/overseas/quotes",
      { tickers: tickers.join(",") },
    ),

  getBuyable: (ticker: string, pcsDit: "1" | "2" | "3" = "1", price?: number, actNo?: string) =>
    apiClient.get<{ rsp_msg: string; result: Record<string, number> }>(
      "/v1/nhplug/overseas/buyable",
      {
        iem_cd: ticker,
        pcs_dit: pcsDit,
        order_type: price ? "00" : "03", // 단가 미지정 시 시장가 기준 가용 (지정가는 단가 필수라 NH 거절)
        ...(price ? { price } : {}),
        ...(actNo ? { act_no: actNo } : {}),
      },
    ),

  syncFills: (days = 7) =>
    apiClient.post<{ auto: { checked: number }; manual: { checked: number; inserted: number; skipped: number } }>(
      "/v1/nhplug/auto/sync-fills",
      { days },
    ),
};
